/**
 * Master Data Service
 * Frontend API client for platform-level reference entities
 * (underlyings, venues, exchange contracts).
 */

import { apiClient, APIResponse } from './client';

// ========== Types ==========

export interface Underlying {
  id: string;
  name: string;
  code: string;
  assetClass: string;
  category: string;
  unitOfMeasure: string;
  currency: string;
  allowedProductTypes: string[];
  isActive: boolean;
}

export interface Venue {
  id: string;
  name: string;
  code: string;
  fullName?: string;
  description?: string;
  isActive: boolean;
}

export interface ExchangeContract {
  id: string;
  name: string;
  code: string;
  venueId: string;
  venueName: string;
  underlyingId?: string;
  fullName?: string;
  description?: string;
  isActive: boolean;
}

// ========== Service ==========

class MasterDataService {
  /**
   * List active underlyings, optionally filtered by asset class.
   */
  async listUnderlyings(assetClass?: string): Promise<Underlying[]> {
    const params = assetClass ? `?asset_class=${encodeURIComponent(assetClass)}` : '';
    const response: APIResponse<Underlying[]> = await apiClient.get(
      `/api/v1/master-data/underlyings${params}`
    );
    return response.data;
  }

  /**
   * List active trading venues.
   */
  async listVenues(): Promise<Venue[]> {
    const response: APIResponse<Venue[]> = await apiClient.get(
      '/api/v1/master-data/venues'
    );
    return response.data;
  }

  /**
   * List active exchange contracts, optionally filtered by venue or underlying.
   */
  async listExchangeContracts(venueId?: string, underlyingId?: string): Promise<ExchangeContract[]> {
    const params = new URLSearchParams();
    if (venueId) params.set('venue_id', venueId);
    if (underlyingId) params.set('underlying_id', underlyingId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response: APIResponse<ExchangeContract[]> = await apiClient.get(
      `/api/v1/master-data/exchange-contracts${qs}`
    );
    return response.data;
  }
}

export const masterDataService = new MasterDataService();
